import request from "../../common/utils/request.js";

type Identifier = string | number;
type UnknownRecord = Record<string, unknown>;

export interface VideoSegment extends UnknownRecord {
  id: Identifier;
  manageId: Identifier;
  knowledgeList: Array<{ name?: string }>;
  startTimeNum: number;
  lengthTime: number;
  showTime: string;
  durationLabel: string;
  isOpen: boolean;
}

interface ProgressMarker {
  offset: number;
  text: string;
}

export interface KnowledgePlayer {
  currentTime: number;
  duration: number;
  play(): Promise<void> | void;
  pause(): void;
  on(event: string, handler: () => void): void;
  off(event: string, handler: () => void): void;
  addProgressDot(time: number, text: string): void;
  removeProgressDot(time: number): void;
}

interface KnowledgeState {
  knowledgeDestroyed: boolean;
  dottingList: VideoSegment[];
  dottingId: Identifier | null;
  thisdottingId: Identifier | null;
  finishTime: number | null;
  progressMarkers: ProgressMarker[];
  markerOffsets: number[];
  marginLeft: number;
  isOpen: boolean;
  currentData: VideoSegment | null;
}

export interface KnowledgeContext extends KnowledgeState {
  id: Identifier;
  player: KnowledgePlayer | null;
  $refs: {
    videoRBIViewport?: HTMLElement;
    videoRBIBox?: HTMLElement;
    kowledgePoints?: { getData(id?: Identifier): Promise<unknown> | void };
  };
  $message: { warning(message: string): void; error(message: string): void };
  $nextTick(): Promise<void>;
  updateKnowledgeMarkers(): void;
}

/** 将章节打点的新接口字段适配到现有卡片，保持展示、跳播及片段结束暂停逻辑。 */
export function normalizeVideoSegments(data: unknown): VideoSegment[] {
  if (!Array.isArray(data)) return [];
  return data.flatMap((value: unknown): VideoSegment[] => {
    if (!value || typeof value !== "object") return [];
    const item = value as UnknownRecord;
    const manageId = item.manageId;
    if (
      (typeof manageId !== "string" || !manageId) &&
      typeof manageId !== "number"
    )
      return [];
    // 起播秒数以 pointSec 为准，空值和非法时间不能被转换为零秒。
    if (
      item.pointSec === null ||
      item.pointSec === undefined ||
      item.pointSec === ""
    )
      return [];
    const startTimeNum = Number(item.pointSec);
    if (!Number.isFinite(startTimeNum) || startTimeNum < 0) return [];
    const length = Number(item.lengthTime);
    const lengthTime = Number.isFinite(length)
      ? Math.max(0, Math.floor(length))
      : 0;
    const showTime = [
      Math.floor(lengthTime / 3600),
      Math.floor(lengthTime / 60) % 60,
      lengthTime % 60,
    ]
      .map((part) => String(part).padStart(2, "0"))
      .join(":");
    return [
      {
        ...item,
        id:
          (typeof item.id === "string" && item.id !== "") ||
          typeof item.id === "number"
            ? item.id
            : manageId,
        manageId,
        coverUrl: item.screenShotLink,
        knowledgeList: item.pointName ? [{ name: String(item.pointName) }] : [],
        startTimeNum,
        lengthTime,
        showTime,
        durationLabel: showTime.replace(/^00:/, ""),
        isOpen: false,
      },
    ];
  });
}

/** 每次移动一张卡片，并按实际容器宽度限制边界，不依赖旧页的固定宽度。 */
export function getKnowledgeScrollOffset(
  current: number,
  direction: -1 | 1,
  contentWidth: number,
  viewportWidth: number
): number {
  const maximum = Math.max(0, contentWidth - viewportWidth);
  return Math.max(-maximum, Math.min(0, current - direction * 168)) || 0;
}

export const videoKnowledge = {
  data(): KnowledgeState {
    return {
      knowledgeDestroyed: false,
      dottingList: [],
      dottingId: null,
      thisdottingId: null,
      finishTime: null,
      progressMarkers: [],
      markerOffsets: [],
      marginLeft: 0,
      isOpen: false,
      currentData: null,
    };
  },
  beforeDestroy(this: KnowledgeContext): void {
    // 请求可能晚于页面销毁返回，避免再操作已释放的播放器或弹出旧提示。
    this.knowledgeDestroyed = true;
    this.player?.off("durationchange", this.updateKnowledgeMarkers);
  },
  methods: {
    /** 知识点加载失败仅提示一次，视频详情和原有学习率连接继续初始化。 */
    async getvideoSegmentList(this: KnowledgeContext): Promise<void> {
      this.dottingList = [];
      this.progressMarkers = [];
      this.marginLeft = 0;
      this.isOpen = false;
      this.currentData = null;
      this.dottingId = null;
      this.thisdottingId = null;
      this.finishTime = null;
      try {
        const response = (await request({
          url: "/course/chapterNodeLocation/queryChapterLocation",
          method: "GET",
          params: { chapterId: this.id, chapterType: 2 },
        })) as unknown as { code?: number; data?: unknown } | undefined;
        if (this.knowledgeDestroyed) return;
        if (
          !response ||
          (response.code !== undefined &&
            response.code !== 0 &&
            response.code !== 200)
        ) {
          throw new Error("知识点接口返回失败");
        }
        this.dottingList = normalizeVideoSegments(response.data);
        this.progressMarkers = this.dottingList.map((item) => ({
          offset: item.startTimeNum,
          text: item.knowledgeList[0]?.name || "知识点",
        }));
      } catch {
        if (this.knowledgeDestroyed) return;
        this.$message.warning("知识点加载失败，请刷新后重试");
      }
      this.updateKnowledgeMarkers();
    },
    /** 播放器和元信息可能晚于列表到达，时长可用后再按西瓜播放器接口添加打点。 */
    initKnowledgePlayer(this: KnowledgeContext, player: KnowledgePlayer): void {
      this.player?.off("durationchange", this.updateKnowledgeMarkers);
      this.player = player;
      player.on("durationchange", this.updateKnowledgeMarkers);
      this.updateKnowledgeMarkers();
    },
    updateKnowledgeMarkers(this: KnowledgeContext): void {
      if (this.knowledgeDestroyed) return;
      const player = this.player;
      if (!player || !Number.isFinite(player.duration) || player.duration <= 0)
        return;
      this.markerOffsets.forEach((offset) => player.removeProgressDot(offset));
      this.markerOffsets = [];
      this.progressMarkers.forEach((marker) => {
        if (marker.offset <= player.duration) {
          player.addProgressDot(marker.offset, marker.text);
          this.markerOffsets.push(marker.offset);
        }
      });
    },
    async jumpTime(this: KnowledgeContext, item: VideoSegment): Promise<void> {
      const player = this.player;
      if (!player) return;
      this.dottingId = item.id;
      this.thisdottingId = item.id;
      this.finishTime =
        item.lengthTime > 0 ? item.startTimeNum + item.lengthTime : null;
      player.currentTime = item.startTimeNum;
      try {
        await player.play();
      } catch {
        this.$message.warning("请点击播放器继续播放");
      }
    },
    /** 与源页一致：选中知识点片段播放完后暂停，保留选中卡片的高亮。 */
    onKnowledgeTimeUpdate(
      this: KnowledgeContext,
      event: { currentTime: number }
    ): void {
      if (
        this.dottingId !== null &&
        this.finishTime !== null &&
        event.currentTime >= this.finishTime
      ) {
        this.player?.pause();
        this.dottingId = null;
        this.finishTime = null;
      }
    },
    leftArrow(this: KnowledgeContext): void {
      const viewport = this.$refs.videoRBIViewport;
      const content = this.$refs.videoRBIBox;
      if (viewport && content) {
        this.marginLeft = getKnowledgeScrollOffset(
          this.marginLeft,
          -1,
          content.scrollWidth,
          viewport.clientWidth
        );
      }
    },
    rightArrow(this: KnowledgeContext): void {
      const viewport = this.$refs.videoRBIViewport;
      const content = this.$refs.videoRBIBox;
      if (viewport && content) {
        this.marginLeft = getKnowledgeScrollOffset(
          this.marginLeft,
          1,
          content.scrollWidth,
          viewport.clientWidth
        );
      }
    },
    /** 同时只展开一个片段；切换时清空旧详情，收起时不再发送重复请求。 */
    async openOrClose(
      this: KnowledgeContext,
      item: VideoSegment
    ): Promise<void> {
      const shouldOpen = this.currentData?.id !== item.id || !item.isOpen;
      this.dottingList.forEach((segment) => {
        segment.isOpen = shouldOpen && segment.id === item.id;
      });
      this.currentData = shouldOpen ? item : null;
      this.isOpen = shouldOpen;
      if (!shouldOpen) return;
      await this.$nextTick();
      try {
        // 列表标识仅用于选中态；知识点关系查询使用打点管理编号，与参考组件一致。
        await this.$refs.kowledgePoints?.getData(item.manageId);
      } catch {
        if (this.currentData?.id === item.id)
          this.$message.error("知识点详情加载失败，请重新展开重试");
      }
    },
  },
};
